import { Household } from '../components/SharePage/AddGuestRail';
import validAddress from './validAddress';

export const findLeader = (household: Household) => household.people.find(p => p.isLeader === true);

export interface SplitHouseholds {
  contactable: Household[];
  unContactable: Household[];
  optedOut: Household[];
}

export const splitHouseholds = (medium: 'email' | 'phone', households?: Household[]): SplitHouseholds => {
  if (!households) {
    return {
      contactable: [],
      unContactable: [],
      optedOut: [],
    };
  }

  // Split them by contactable and unContactable
  const splitted = households.reduce<SplitHouseholds>(
    (initialVal, household) => {
      const leader = household.people.find(person => person.isLeader)!;
      if (leader[medium]) {
        const optedOut = medium === 'email' ? leader.preferences?.emailOptOut : leader.preferences?.smsOptOut;
        if (optedOut) {
          return {
            ...initialVal,
            optedOut: [...initialVal.optedOut, household],
          };
        }
        return {
          ...initialVal,
          contactable: [...initialVal.contactable, household],
        };
      }
      return {
        ...initialVal,
        unContactable: [...initialVal.unContactable, household],
      };
    },
    { contactable: [], unContactable: [], optedOut: [] }
  );

  // Sort the lists by name
  return {
    contactable: sortHouseholds(splitted.contactable, 'email'),
    unContactable: sortHouseholds(splitted.unContactable, 'firstName'),
    optedOut: sortHouseholds(splitted.optedOut, 'firstName'),
  };
};

export const findHouseholdsByRsvp = (households?: Household[], eventId?: string, withRSVP = false): Household[] => {
  if (!households || !eventId) {
    return [];
  }

  // Return households that have not RSVPed to the provided event.
  return households.filter(h => {
    const leader = h.people.find(person => person.isLeader)!;
    const eventInvite = leader.invitations.find(invite => invite.eventId === eventId);

    if (eventInvite?.rsvp === null) {
      return !withRSVP;
    }
    return withRSVP;
  });
};

export const findHouseholdsWithAddress = (households?: Household[]) => {
  if (!households) {
    return [];
  }
  // returns households with address
  return households.filter(h => validAddress(h.address));
};

export const findHouseholdsWithoutAddress = (households?: Household[]) => {
  if (!households) {
    return [];
  }

  // returns households with no address
  return households.filter(h => !validAddress(h.address));
};

export interface ActionCompletedHouseholds extends SplitHouseholds {
  actionCompleted: Household[];
}

export const findHouseholdsforAddrsCollection = (
  medium: 'email' | 'phone',
  households?: Household[]
): ActionCompletedHouseholds => {
  if (!households) {
    return {
      contactable: [],
      unContactable: [],
      optedOut: [],
      actionCompleted: [],
    };
  }
  return {
    actionCompleted: sortHouseholds(findHouseholdsWithAddress(households), 'firstName'),
    ...splitHouseholds(medium, findHouseholdsWithoutAddress(households)),
  };
};

// Sorts households such that households with emails come first, then sorts by first name.

export const sortHouseholds = (households?: Household[], sortBy: 'email' | 'phone' | 'firstName' = 'phone') => {
  if (!households) {
    return [];
  }

  const sortedHouseholds = households.sort((householdA: Household, householdB: Household) => {
    const leaderA = householdA.people.find(person => person.isLeader);
    const leaderB = householdB.people.find(person => person.isLeader);

    if (leaderA![sortBy] && !leaderB![sortBy]) {
      return -1;
    } else if (!leaderA![sortBy] && leaderA![sortBy]) {
      return 1;
    } else {
      if (leaderA!.firstName?.toLowerCase() && leaderB!.firstName?.toLowerCase())
        if (leaderA!.firstName.toLowerCase() < leaderB!.firstName.toLowerCase()) {
          return -1;
        } else if (leaderB!.firstName.toLowerCase() < leaderA!.firstName.toLowerCase()) {
          return 1;
        }
      return 0;
    }
  });

  return sortedHouseholds;
};

export const getEditLabel = (sequence: string, isBulk: boolean = false) => {
  const emailExtension = isBulk ? ' addresses ' : ' address ';
  const phoneExtension = isBulk ? ' numbers' : ' number';

  return 'Add ' + sequence + (sequence === 'email' ? emailExtension : phoneExtension);
};
